import { Badge } from "@/components/ui/Badge";
import { Checkbox } from "@/components/ui/Checkbox";
import { Text } from "@/components/ui/Text";
import { type RowSelection } from "@/hooks/useRowSelection";
import { pluralize } from "@/lib/format";
import { formatPlatform } from "@/lib/formatPlatform";
import { cn } from "@/lib/utils";
import { Sparkline } from "@/pages/costs/Sparkline";
import type { RiskSignal } from "@gram/client/models/components/risksignal.js";
import { ruleCategoryLabel } from "../policy-data";
import { CategoryLabel } from "../risk-ui";
import { getRuleTitleFallback, scoreToRating } from "../risk-utils";
import {
  SCORE_TEXT_COLOR,
  SEVERITY_ACCENT,
  SEVERITY_GROUP_LABEL,
  UNATTRIBUTED_GROUP_KEY,
  trendPercent,
  type SignalGroup,
  type SignalGroupMode,
  type SignalSeverity,
} from "./signals-helpers";

/**
 * Finding-count trend for a signal row: the within-window percentage (end of
 * the sparkline against its start, so the number moves with the drawn line),
 * or a "new" badge when the window opened with nothing to compare against.
 */
export function SignalTrend({
  sparkline,
}: {
  sparkline: number[];
}): JSX.Element {
  const trend = trendPercent(sparkline);
  if (trend === null) {
    return (
      <Badge variant="information" size="sm">
        <Badge.Text>new</Badge.Text>
      </Badge>
    );
  }
  if (Math.round(trend) === 0) {
    return <span className="text-muted-foreground text-xs">±0%</span>;
  }
  const rising = trend > 0;
  return (
    <span
      className={cn(
        "text-xs font-medium tabular-nums",
        rising ? "text-destructive" : "text-success-foreground",
      )}
    >
      {rising ? "+" : ""}
      {Math.round(trend)}%
    </span>
  );
}

function serverLabel(
  serverId: string,
  serverNames: ReadonlyMap<string, string> | null,
): string {
  return serverNames?.get(serverId) ?? serverId.slice(0, 8);
}

function groupLabel(
  group: SignalGroup,
  mode: SignalGroupMode,
  serverNames: ReadonlyMap<string, string> | null,
): string {
  switch (mode) {
    case "severity":
      return SEVERITY_GROUP_LABEL[group.key as SignalSeverity] ?? group.key;
    case "category":
      return ruleCategoryLabel(group.key);
    case "team":
    case "app":
    case "principal":
      return group.key === UNATTRIBUTED_GROUP_KEY ? "Unattributed" : group.key;
    case "server":
      return group.key === UNATTRIBUTED_GROUP_KEY
        ? "No MCP server"
        : serverLabel(group.key, serverNames);
  }
}

const MAX_SERVER_LABELS = 2;

/** `server · tool` line under a signal, capped so a wide signal stays one row. */
function SignalServers({
  signal,
  serverNames,
}: {
  signal: RiskSignal;
  serverNames: ReadonlyMap<string, string>;
}): JSX.Element {
  const shown = signal.mcpServerIds.slice(0, MAX_SERVER_LABELS);
  const hidden = signal.mcpServerIds.length - shown.length;
  const names = shown.map((id) => serverLabel(id, serverNames)).join(", ");
  const tools = signal.toolNames.slice(0, 3).join(", ");
  const toolOverflow = signal.toolNames.length - 3;
  // Servers and tools are separate lists, so they only read as `server · tool`
  // when there is one server; across several, the tools are labeled as a
  // separate list rather than implied to belong to each server.
  const toolsLabel = signal.mcpServerIds.length > 1 ? "tools: " : "";
  return (
    <div className="text-muted-foreground flex min-w-0 items-center gap-2 font-mono text-xs">
      <span
        aria-hidden
        className="bg-[var(--color-brand-blue-500)] size-2 shrink-0"
      />
      <span className="truncate">
        {names}
        {hidden > 0 ? ` +${hidden}` : ""}
        {tools
          ? ` · ${toolsLabel}${tools}${toolOverflow > 0 ? ` +${toolOverflow}` : ""}`
          : ""}
      </span>
    </div>
  );
}

function SignalRow({
  signal,
  active,
  selection,
  onSelect,
  serverNames,
}: {
  signal: RiskSignal;
  active: boolean;
  selection: RowSelection<RiskSignal>;
  onSelect: (signal: RiskSignal) => void;
  serverNames: ReadonlyMap<string, string> | null;
}): JSX.Element {
  const rating = scoreToRating(signal.riskScore);
  const usersLine =
    signal.teams > 0
      ? `${pluralize(signal.users, "user")} · ${pluralize(signal.teams, "team")}`
      : pluralize(signal.users, "user");
  // The open action and the selection checkbox are sibling controls: nesting
  // the checkbox inside a role="button" row would flatten it out of the
  // accessibility tree (button descendants are presentational) and make it
  // unreachable for keyboard/screen-reader users.
  return (
    <div
      style={{ borderLeftColor: SEVERITY_ACCENT[rating] }}
      className={cn(
        "bg-card hover:bg-muted/40 divide-border flex w-full items-stretch divide-x border-l-2 transition-colors",
        active && "bg-muted/30",
      )}
    >
      <div
        className="flex shrink-0 cursor-pointer items-center px-3"
        onClick={(e) => {
          // The whole checkbox cell is a selection target: clicks anywhere in
          // it toggle the row. The checkbox itself already toggles via
          // onCheckedChange, so only the padding around it toggles here.
          if ((e.target as HTMLElement).closest("button")) return;
          selection.toggle(signal.key);
        }}
      >
        <Checkbox
          checked={selection.isSelected(signal.key)}
          onCheckedChange={() => selection.toggle(signal.key)}
          aria-label="Select signal"
        />
      </div>
      <div
        role="button"
        tabIndex={0}
        aria-label={`Open details for ${getRuleTitleFallback(signal.ruleId)}`}
        onClick={() => onSelect(signal)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(signal);
          }
        }}
        className="divide-border flex min-w-0 flex-1 cursor-pointer items-stretch divide-x text-left"
      >
        <div className="flex w-28 shrink-0 flex-col items-start justify-center gap-1 px-4 py-3">
          <span
            className="font-display text-4xl leading-none font-thin"
            style={{ color: SCORE_TEXT_COLOR[rating] }}
          >
            {signal.riskScore.toFixed(1)}
          </span>
          <span
            className="font-mono text-[11px] tracking-wide uppercase"
            style={{ color: SCORE_TEXT_COLOR[rating] }}
          >
            {signal.severity}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-4 py-3">
          <Text className="font-semibold">
            {getRuleTitleFallback(signal.ruleId)}
          </Text>
          {signal.description && (
            <Text small muted className="line-clamp-2">
              {signal.description}
            </Text>
          )}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <CategoryLabel
              source={signal.detectionSources[0]}
              ruleId={signal.ruleId}
            />
            {signal.apps.map((app) => (
              <span
                key={app}
                className="text-muted-foreground inline-flex items-center gap-1.5 font-mono text-xs"
              >
                <span aria-hidden className="bg-foreground/70 size-2" />
                {formatPlatform(app)}
              </span>
            ))}
          </div>
          {serverNames && signal.mcpServerIds.length > 0 ? (
            <SignalServers signal={signal} serverNames={serverNames} />
          ) : null}
        </div>
        <div className="flex w-54 shrink-0 flex-col justify-center gap-1 px-4 py-3">
          <div className="flex items-baseline justify-between">
            <span className="text-lg font-normal tabular-nums">
              {signal.findings.toLocaleString()}
            </span>
            <SignalTrend sparkline={signal.sparkline} />
          </div>
          <Sparkline values={signal.sparkline} width={184} height={28} />
          <span className="text-muted-foreground font-mono text-xs">
            {usersLine}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The ranked signal list, optionally sectioned by severity or category. The
 * server caps signals at 200 rows, so a plain map (no virtualizer) is fine.
 */
export function SignalsList({
  groups,
  mode,
  selectedKey,
  selection,
  onSelect,
  serverNames = null,
}: {
  groups: SignalGroup[];
  mode: SignalGroupMode;
  selectedKey: string | null;
  selection: RowSelection<RiskSignal>;
  onSelect: (signal: RiskSignal) => void;
  // Set only when MCP-scoped guardrails are enabled; turns on the server/tool
  // line under each signal and names the server groups.
  serverNames?: ReadonlyMap<string, string> | null;
}): JSX.Element {
  return (
    <div className="space-y-6">
      {groups.map((group) => {
        const label = groupLabel(group, mode, serverNames);
        return (
          <div key={group.key} className="space-y-2">
            {label && (
              <Text small muted className="font-medium uppercase">
                {label} · {group.signals.length}
              </Text>
            )}
            <div className="border-border divide-border divide-y overflow-hidden rounded-lg border">
              {group.signals.map((signal) => (
                <SignalRow
                  key={signal.key}
                  signal={signal}
                  active={signal.key === selectedKey}
                  selection={selection}
                  onSelect={onSelect}
                  serverNames={serverNames}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
